import { FunctionComponent, lazy, LazyExoticComponent, Suspense } from 'react'

import { ProcessingText } from 'core-components/library/loader'
import { loadMeta, loadMetaByLoader } from 'core-components/sitebuilder/helpers/MetaSelector'

export type TLoadingComponent = FunctionComponent<any> & {
  default: FunctionComponent & { properties: any }
  [key: string]: any
}
type TLoadingFunction = LazyExoticComponent<TLoadingComponent>
export type TWidgetLoadFunction = (name: string, key: string) => Promise<TLoadingComponent>

const cache: { [key: string]: TLoadingFunction } = {}

// TODO CHECK AFTER MF
// @ts-ignore
if (!window.globalProjectLoaders) {
  // @ts-ignore
  window.globalProjectLoaders = {}
}

// @ts-ignore
export const projectLoaders: { [key: string]: TWidgetLoadFunction } =
  // @ts-ignore
  window.globalProjectLoaders

export const getLazyWidget = (
  key: string,
  getLoadPromise: () => Promise<any>
): TLoadingFunction => {
  if (!cache[key]) {
    cache[key] = lazy(getLoadPromise)
  }
  return cache[key] as TLoadingFunction
}

export const loadTemplate = (name: string): Promise<TLoadingComponent> | null => {
  const keys = Object.keys(projectLoaders)
  for (const key of keys) {
    if (name.startsWith(key)) {
      const loadFunction = projectLoaders[key]
      return loadFunction(name, key)
    }
  }
  return null
}

export const lazyRenderWidget = (name: string, props: object, widgetNameForMeta?: string) => {
  try {
    const Widget = getComponent(name)
    if (Widget) {
      if (widgetNameForMeta) {
        loadMeta(widgetNameForMeta, name)
      }
      return (
        <Suspense fallback={<ProcessingText showDelay={3000} />}>
          <Widget {...props} />
        </Suspense>
      )
    }
    throw new Error('Widget not found')
  } catch {
    console.error(`Wrong widget name: ${name}. Check repository path name`)
  }

  return null
}

export const lazyRenderWidgetByLoader = ({
  id,
  loader,
  props,
  loadMeta,
}: {
  id: string
  loader: () => Promise<any>
  props: object
  loadMeta?: boolean
}) => {
  const Widget = getLazyWidget(id, loader)

  if (Widget) {
    if (loadMeta) {
      loadMetaByLoader({ loader, id })
    }

    return (
      <Suspense key={id} fallback={<ProcessingText showDelay={3000} />}>
        <Widget {...props} />
      </Suspense>
    )
  }

  return <div>Error: {id}</div>
}

const getComponent = (name: string) => {
  const keys = Object.keys(projectLoaders)
  for (const key of keys) {
    if (name.startsWith(key)) {
      return getLazyWidget(name, () => {
        const loadFunction = projectLoaders[key]
        return loadFunction(name, key).catch((err) => {
          // check if the window has already been refreshed
          const hasRefreshedDate = window.sessionStorage.getItem('retry-lazy-refreshed')
          const refreshDate = hasRefreshedDate ? new Date(hasRefreshedDate) : NaN
          const minute = 60 * 1000
          const now = new Date()
          const needRefresh =
            !hasRefreshedDate ||
            // @ts-ignore
            isNaN(refreshDate) ||
            now.getTime() - (refreshDate as Date).getTime() > minute * 5 // can reload after 5 minutes
          if (needRefresh) {
            // not been refreshed yet
            window.sessionStorage.setItem('retry-lazy-refreshed', now.toString()) // we are now going to refresh
            return window.location.reload() // refresh the page
          }

          console.error(`LazyLoad error for ${name}:`, err.message)

          return {
            default: () => {
              return <div>{err.message}</div>
            },
          }
        })
      })
    }
  }
  return null
}
